import { useRef, useState } from 'react';
import { Popover } from 'radix-ui';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import { useWorkspace } from '../context';
import { useIntlLocale } from '../intl-locale';
import { WorkspaceIcon } from '../icons';
import { useWorkspaceFind } from '../retrieval';
import type { WorkspaceFindHit, WorkspaceFindKind } from '../types';
import { projectsApi } from './api';
import { itemKind } from './labels';
import { projectScope } from './use-projects';
import type { ProjectDetail } from './types';

const KINDS: WorkspaceFindKind[] = ['result', 'file', 'meeting'];

function hitKind(hit: WorkspaceFindHit, locale?: string) {
  return itemKind(
    {
      kind: hit.kind === 'result' ? 'result' : hit.kind === 'meeting' ? 'meeting' : 'file',
      resultKind:
        typeof hit.metadata?.resultKind === 'string'
          ? (hit.metadata.resultKind as string)
          : undefined,
    },
    locale,
  );
}

/**
 * Add on the project page: anything the person can already open. It stays as
 * open or as private as it was; the row then says who added it.
 */
export function ProjectAddMenu({
  project,
  onChange,
  first = false,
}: {
  project: ProjectDetail;
  onChange: (next: ProjectDetail) => void;
  /** An empty project's first step: "Add work you already have". */
  first?: boolean;
}) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const locale = useIntlLocale();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const found = useWorkspaceFind(open, query, KINDS, ws.user?.id ?? '');
  const already = new Set(project.items.map((item) => `${item.kind}:${item.id}`));
  const hits = found.hits.filter(
    (hit) => KINDS.includes(hit.kind) && !already.has(`${hit.kind}:${hit.id}`),
  );
  const add = async (hit: WorkspaceFindHit) => {
    setBusy(hit.id);
    const inScope = projectScope();
    try {
      const next = await projectsApi.addItem(project.id, {
        kind: hit.kind as 'result' | 'file' | 'meeting',
        id: hit.id,
      });
      if (!inScope()) return;
      onChange(next);
      ws.notify(
        formatCopy(copy.addedTo, { item: hit.title, project: project.name }),
        null,
        'success',
      );
      setOpen(false);
      setQuery('');
    } catch {
      ws.notify(formatCopy(copy.couldntAdd, { name: hit.title }), null, 'error');
    } finally {
      setBusy('');
    }
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className={first ? 'ws-project-step' : 'ws-project-add'}>
        <WorkspaceIcon name="plus" size={14} />
        {first ? copy.addExistingWork : copy.add}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="ws-project-place-panel pc-portal"
          align="end"
          sideOffset={8}
          collisionPadding={16}
          aria-label={formatCopy(copy.addTo, { name: project.name })}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            input.current?.focus();
          }}
        >
          <header>
            <strong>{formatCopy(copy.addTo, { name: project.name })}</strong>
            <small>{copy.addAnything}</small>
          </header>
          <label className="ws-field-search sm">
            <WorkspaceIcon name="search" size={14} />
            <input
              ref={input}
              type="search"
              aria-label={copy.searchYourWork}
              placeholder={copy.searchDocuments}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <div className="ws-project-place-list" role="listbox" aria-label={copy.yourWork}>
            {found.loading && !hits.length && (
              <p className="ws-project-empty" role="status">
                {copy.looking}
              </p>
            )}
            {!found.loading && !hits.length && (
              <p className="ws-project-empty">
                {query.trim() ? copy.nothingMatchesChat : copy.typeToFind}
              </p>
            )}
            {hits.map((hit) => {
              const kind = hitKind(hit, locale);
              return (
                <button
                  key={`${hit.kind}:${hit.id}`}
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="ws-project-place-row"
                  disabled={busy === hit.id}
                  onClick={() => void add(hit)}
                >
                  <span className="ws-project-item-kind">
                    <WorkspaceIcon name={kind.icon} size={15} />
                  </span>
                  <span>
                    <b>{hit.title}</b>
                    <small>{kind.label}</small>
                  </span>
                </button>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
